import type { PlannerProject, WardrobeModule } from "../types";
import {
  cornerDoorWidthMm, cornerFillerMm, cornerNeighbourFillerMm, cornerRun, cornerSide,
  CORNER_DOOR_THICKNESS_MM, CORNER_FILLER_MAX_MM, CORNER_FILLER_MIN_MM
} from "./corner";
import { handleCenterFromOpeningEdgeMm, handleLengthMm, handleOrientation, handleProjectionMm } from "./handles";
import { cornerNeighbour } from "./layout";
import { hingedDoorLeafLayout } from "./moduleRules";

/*
 * Доборы углового под двери и ручки (видео Сергея 31.10). Петли обеих дверей —
 * у угла. Открытая на 90° накладная дверь встаёт перед торцом своего корпуса и
 * выступает за него на толщину фасада, поэтому:
 * — дверь углового ложится вдоль фасада соседа: добор углового не меньше
 *   толщины двери, плюс вынос ручки соседа, если ручка у неё на пути;
 * — дверь соседа ложится вдоль фасада углового: добор соседа — так же, по ручке
 *   двери углового.
 * Зазор сверху — 5 мм, шаг — 5 мм.
 */
const CLEARANCE_MM = 5;
/** Кромка двери отстоит от края корпуса или добора. */
const LEAF_REVEAL_MM = 2;

const neededFiller = (projectionMm: number) => Math.min(CORNER_FILLER_MAX_MM, Math.max(CORNER_FILLER_MIN_MM,
  Math.ceil((CORNER_DOOR_THICKNESS_MM + projectionMm + CLEARANCE_MM) / 5) * 5));

/**
 * Где ручка двери поперёк створки: ближний и дальний край, мм от кромки у угла.
 * Петли у угла — ручка у дальней кромки, петли у дальнего торца — у ближней.
 */
const handleSpanFromCornerEdge = (module: WardrobeModule, doorWidth: number, doorHeight: number, hingeAtCorner: boolean) => {
  const half = handleOrientation(module) === "horizontal" ? handleLengthMm(module, doorWidth, doorHeight) / 2 : 7;
  const center = handleCenterFromOpeningEdgeMm(module, doorWidth, doorHeight);
  const fromCorner = hingeAtCorner ? doorWidth - center : center;
  return { near: fromCorner - half, far: fromCorner + half };
};

/** Соседняя створка у угла: её ширина и где у неё петли. null — распашной двери у соседа нет. */
const neighbourCornerLeaf = (project: PlannerProject, corner: WardrobeModule) => {
  if (corner.type !== "corner45" || corner.door !== "hinged") return null;
  const neighbour = cornerNeighbour(project, corner);
  if (!neighbour || neighbour.door !== "hinged" || (neighbour.type !== "single" && neighbour.type !== "double")) return null;
  const leaves = hingedDoorLeafLayout(neighbour);
  // Угол — слева от соседа, если угловой у задней стены справа или у боковой слева.
  const cornerAtLeft = cornerRun(corner) === "back" ? cornerSide(corner) === "right" : cornerSide(corner) === "left";
  const leaf = cornerAtLeft ? leaves[0] : leaves.at(-1);
  if (!leaf) return null;
  return { neighbour, widthMm: leaf.widthMm, hingeAtCorner: (leaf.hingeSide < 0) === cornerAtLeft };
};

/**
 * Добор углового: открытая дверь углового (петли у угла) ложится вдоль фасада
 * соседа и не должна задеть его дверь и ручку. null — соседа с распашной
 * дверью нет, ограничения нет.
 */
export const cornerFillerForHandlesMm = (project: PlannerProject, corner: WardrobeModule) => {
  const leaf = neighbourCornerLeaf(project, corner);
  if (!leaf) return null;
  const projection = handleProjectionMm(leaf.neighbour.handle);
  if (projection <= 0) return neededFiller(0);
  const handle = handleSpanFromCornerEdge(leaf.neighbour, leaf.widthMm, leaf.neighbour.height, leaf.hingeAtCorner);
  // Ручка соседа — от плоскости дверей углового: добор соседа и зазор кромки.
  const handleNear = cornerNeighbourFillerMm(corner) + LEAF_REVEAL_MM + handle.near;
  // Открытая дверь углового доходит вдоль соседа до своей ширины от плоскости корпуса.
  const reach = cornerDoorWidthMm(corner) - CORNER_DOOR_THICKNESS_MM;
  return neededFiller(handleNear > reach + CLEARANCE_MM ? 0 : projection);
};

/**
 * Добор соседа: открытая дверь соседа с петлями у угла ложится вдоль фасада
 * углового и не должна задеть его дверь и ручку. null — у соседа петли не у
 * угла или нет распашной двери: его дверь к фасаду углового не подходит.
 */
export const cornerNeighbourFillerForHandlesMm = (project: PlannerProject, corner: WardrobeModule) => {
  const leaf = neighbourCornerLeaf(project, corner);
  if (!leaf || !leaf.hingeAtCorner) return null;
  const projection = handleProjectionMm(corner.handle);
  if (projection <= 0) return neededFiller(0);
  const doorWidth = cornerDoorWidthMm(corner);
  const handle = handleSpanFromCornerEdge(corner, doorWidth, corner.height, true);
  // Ручка углового — от плоскости дверей соседа: добор углового и зазор кромки.
  const handleNear = cornerFillerMm(corner) + LEAF_REVEAL_MM + handle.near;
  const reach = leaf.widthMm - CORNER_DOOR_THICKNESS_MM;
  return neededFiller(handleNear > reach + CLEARANCE_MM ? 0 : projection);
};

/** Доборы меньше нужного под двери и ручки: сколько нужно и сколько сейчас; пусто — всё в порядке. */
export const cornerFillerShortages = (project: PlannerProject, corner: WardrobeModule) => {
  const result: Array<{ which: "corner" | "neighbour"; needed: number; current: number }> = [];
  const own = cornerFillerForHandlesMm(project, corner);
  if (own !== null && cornerFillerMm(corner) < own) result.push({ which: "corner", needed: own, current: cornerFillerMm(corner) });
  const neighbour = cornerNeighbourFillerForHandlesMm(project, corner);
  if (neighbour !== null && cornerNeighbourFillerMm(corner) < neighbour) {
    result.push({ which: "neighbour", needed: neighbour, current: cornerNeighbourFillerMm(corner) });
  }
  return result;
};
